<template>
  <t-upper-nav-menu ref="child">
    <template #trigger>
      <button
        @click="onProfileButtonClick()"
        class="tw-block hover:tw-text-primary-grey-700"
        data-test-id="upper-nav-profile-menu-button"
        v-tippy="{
          content: $t('tooltip-profile-menu'),
          placement: 'bottom',
        }"
      >
        <cb-icon-user />
      </button>
    </template>
    <template #title>
      <div class="tw-flex tw-gap-2 tw-items-center tw-px-2 tw-py-1">
        <div
          class="tw-text-primary-grey-700 tw-p-2 tw-rounded-full tw-bg-white"
        >
          <!-- original was 30px; nearest supported size is 32 -->
          <cb-icon-user :width="32" />
        </div>
        <div class="tw-overflow-hidden">
          <div class="tw-text-xl tw-font-semibold tw-truncate">
            <span
              v-if="name.length > 25"
              v-tippy="{
                content: name,
                placement: 'bottom',
              }"
            >
              {{ name }}
            </span>
            <span v-else>
              {{ name }}
            </span>
          </div>
          <div class="tw-truncate">
            <span
              v-if="email.length > 25"
              v-tippy="{
                content: email,
                placement: 'bottom',
              }"
            >
              {{ email }}
            </span>
            <span v-else>
              {{ email }}
            </span>
          </div>
          <div class="tw-truncate" v-if="displayRole !== ''">
            <div class="tw-flex tw-gap-1" data-test-id="upper-nav-role-display">
              {{ $t(displayRole) }}
              <span
                v-tippy="displayRoleTooltip(role)"
                class="tw-mt-0.5 tw-text-primary-grey-100"
                data-test-id="upper-nav-role-display-tooltip"
              >
                <cb-icon-information :width="18" color="currentColor" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div>
        <div>
          <router-link
            custom
            v-slot="{ href }"
            :to="`/profile/security?id=${uuid}`"
          >
            <a
              :href="href"
              target="_blank"
              class="tw-flex tw-items-center tw-gap-1 tw-border-b tw-pb-3 tw-px-2 tw-border-primary-grey-100 hover:tw-text-primary-grey-700"
              data-test-id="upper-nav-security-settings-button"
              @click="onSettingsButtonClick"
            >
              <div><cb-icon-settings :width="20" /></div>
              <div>{{ $t("menu-item-security-settings") }}</div>
            </a>
          </router-link>
          <router-link v-slot="{ href }" :to="logoutTarget" custom>
            <a
              :href="href"
              class="tw-flex tw-items-center tw-gap-1 tw-pt-3 tw-px-2 tw-text-system-error-500 hover:tw-text-system-error-400"
              data-test-id="upper-nav-sign-out-button"
              @click="onSignOutButtonClick"
            >
              <div><cb-icon-logout :width="20" /></div>
              <div>{{ $t("menu-item-sign-out") }}</div>
            </a>
          </router-link>
        </div>
      </div>
    </template>
  </t-upper-nav-menu>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import TUpperNavMenu from "./TUpperNavMenu.vue";
import { trackEvent } from "@/utils/track-event";
import { useStore } from "vuex";
import { displayRoleStringByApiSlug } from "@/utils/roles.ts";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useFlag } from "@/composables/useFlags/useFlag";

const child = ref<InstanceType<typeof TUpperNavMenu>>();

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const authUser = computed(() => store.getters["authentication/authUser"]);
const { isFeatureAvailable: isLogoutWithRedirectEnabled } = useFlag(
  "LOGOUT_WITH_REDIRECT",
);

const name = computed(() => authUser.value?.accountname);
const email = computed(() => authUser.value?.email);
const uuid = computed(() => authUser.value?.uuid);
const role = computed(() => authUser.value?.role);
const displayRole = computed(() => displayRoleStringByApiSlug(role.value));
const displayRoleTooltip = (role: string) => {
  //TODO @Rob to move these to roles.ts when super-admin is merged to `main`:
  const dittoRoleStrings: Record<string, string> = {
    "admin-ro": "tooltip-read-only",
    admin: "tooltip-administrator",
    "super-admin": "tooltip-super-administrator",
  };
  const tooltipContent = dittoRoleStrings[role];
  if (tooltipContent) {
    return {
      content: t(tooltipContent),
      placement: "top",
    };
  }
  return undefined;
};

const logoutTarget = computed(() => {
  const enabled = !!isLogoutWithRedirectEnabled?.value;
  const redirect = route.fullPath || "/";
  if (enabled) {
    return { name: "logout", query: { redirect } };
  }
  return { name: "logout" };
});

const onProfileButtonClick = () => {
  trackEvent({
    category: "Upper navigation",
    action: "profile settings button clicked",
    name: "",
  });

  child.value?.toggleMenuVisibility();
};

const onSettingsButtonClick = () => {
  trackEvent({
    category: "Upper navigation",
    action: "security settings button clicked",
    name: "",
  });
};

const onSignOutButtonClick = () => {
  trackEvent({
    category: "Upper navigation",
    action: "sign out button clicked",
    name: "",
  });
};
</script>
